import React, { useState, useEffect } from 'react';
import API_BASE from '../config';

function UserManagement({ accessCode }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newUser, setNewUser] = useState({ name: '', email: '', role: 'representative', class: '1A' });
  const [editingEmail, setEditingEmail] = useState(null);
  const [editEmailValue, setEditEmailValue] = useState('');
  const [classes, setClasses] = useState([]);
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filterText, setFilterText] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterClass, setFilterClass] = useState('all');

  useEffect(() => {
    fetchUsers();
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/config`);
      const data = await response.json();
      setClasses(data.classes || ['1A','1B','1C','2A','2B','2C','3A','3B','3C']);
    } catch (err) { console.error('Failed to load config:', err); }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/users`, {
        headers: { 'x-access-code': accessCode }
      });
      const data = await response.json();
      setUsers(data);
      setLoading(false);
    } catch (err) {
      setError('Errore nel/nella caricamento utenti');
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    setError('');
    setSuccess('');
    
    if (!newUser.name.trim()) {
      setError('Inserire un nome.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newUser.email)) {
      setError('Inserire un indirizzo email valido.');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'x-access-code': accessCode
        },
        body: JSON.stringify(newUser)
      });
      
      if (response.ok) {
        const created = await response.json();
        setSuccess(`Codice di accesso creato: ${created.code}`);
        setNewUser({ name: '', email: '', role: 'representative', class: '1A' });
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella creazione utente');
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo codice di accesso?')) return;
    
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/admin/users/${code}`, {
        method: 'DELETE',
        headers: { 'x-access-code': accessCode }
      });
      
      if (response.ok) {
        setSuccess('Codice di accesso eliminato.');
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella eliminazione utente');
    }
  };

  const handleRegenerate = async (code) => {
    if (!window.confirm('Rigenerare questo codice di accesso? Il vecchio codice smetterà di funzionare.')) return;
    
    setError('');
    setSuccess('');
    
    try {
      const response = await fetch(`${API_BASE}/admin/users/${code}/regenerate`, {
        method: 'PUT',
        headers: { 'x-access-code': accessCode }
      });
      
      if (response.ok) {
        const updated = await response.json();
        setSuccess(`Nuovo codice generato: ${updated.code}`);
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nel/nella rigenerazione codice');
    }
  };

  const handleSaveEmail = async (code) => {
    setError('');
    setSuccess('');
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editEmailValue)) {
      setError('Inserire un indirizzo email valido.');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/admin/users/${code}/email`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-code': accessCode },
        body: JSON.stringify({ email: editEmailValue })
      });
      if (response.ok) {
        setSuccess('Email aggiornata.');
        setEditingEmail(null);
        fetchUsers();
      } else {
        const data = await response.json();
        setError(data.error);
      }
    } catch (err) {
      setError('Errore nell\'aggiornamento email');
    }
  };

  if (loading) return <p>Caricamento...</p>;

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIndicator = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const getRoleLabel = (user) => {
    return user.role === 'organizer' ? 'Organizzatore' : `Rapp. - ${user.class}`;
  };

  const filteredAndSortedUsers = [...users]
    .filter(user => {
      const matchesText = !filterText || 
        (user.name || '').toLowerCase().includes(filterText.toLowerCase()) ||
        (user.email || '').toLowerCase().includes(filterText.toLowerCase());
      const matchesRole = filterRole === 'all' || user.role === filterRole;
      const matchesClass = filterClass === 'all' || user.class === filterClass;
      return matchesText && matchesRole && matchesClass;
    })
    .sort((a, b) => {
      let valA, valB;
      if (sortField === 'name') {
        valA = (a.name || '').toLowerCase();
        valB = (b.name || '').toLowerCase();
      } else if (sortField === 'email') {
        valA = (a.email || '').toLowerCase();
        valB = (b.email || '').toLowerCase();
      } else if (sortField === 'role') {
        valA = getRoleLabel(a).toLowerCase();
        valB = getRoleLabel(b).toLowerCase();
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  return (
    <div>
      <h2>Gestione Codici di Accesso</h2>
      
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="config-section">
        <h3>Crea Nuovo Codice di Accesso</h3>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ flex: 1, minWidth: '150px' }}>
            <label>Nome</label>
            <input
              type="text"
              value={newUser.name}
              onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
              placeholder="e.g. Mrs. Rossi"
            />
          </div>
          <div className="form-group" style={{ flex: 1, minWidth: '180px' }}>
            <label>Email</label>
            <input
              type="email"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              placeholder="email@scuola.it"
            />
          </div>
          <div className="form-group" style={{ width: '180px' }}>
            <label>Ruolo</label>
            <select
              value={newUser.role}
              onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
            >
              <option value="organizer">Organizzatore</option>
              <option value="representative">Rappresentante di Classe</option>
            </select>
          </div>
          {newUser.role === 'representative' && (
            <div className="form-group" style={{ width: '120px' }}>
              <label>Classe</label>
              <select
                value={newUser.class}
                onChange={(e) => setNewUser({ ...newUser, class: e.target.value })}
              >
                {classes.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <button className="btn-primary" onClick={handleCreate} style={{ height: '40px' }}>
            Crea
          </button>
        </div>
      </div>

      <div className="config-section">
        <h3>Codici di Accesso Esistenti</h3>
        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Cerca per nome o email..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            style={{ flex: 1, minWidth: '180px', padding: '6px 10px' }}
          />
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            style={{ width: '180px', padding: '6px 10px' }}
          >
            <option value="all">Tutti i ruoli</option>
            <option value="organizer">Organizzatore</option>
            <option value="representative">Rappresentante</option>
          </select>
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            style={{ width: '120px', padding: '6px 10px' }}
          >
            <option value="all">Tutte le classi</option>
            {classes.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <table className="dashboard-table">
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('name')}>Nome{getSortIndicator('name')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('email')}>Email{getSortIndicator('email')}</th>
              <th style={{ cursor: 'pointer' }} onClick={() => handleSort('role')}>Ruolo{getSortIndicator('role')}</th>
              <th>Codice</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedUsers.map((user) => (
              <tr key={user.code}>
                <td><strong>{user.name}</strong></td>
                <td>
                  {editingEmail === user.code ? (
                    <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                      <input type="email" value={editEmailValue} onChange={(e) => setEditEmailValue(e.target.value)}
                        style={{ fontSize: '12px', padding: '3px 6px', width: '160px' }} />
                      <button className="btn-primary" onClick={() => handleSaveEmail(user.code)} style={{ fontSize: '11px', padding: '3px 8px' }}>✓</button>
                      <button className="btn-secondary" onClick={() => setEditingEmail(null)} style={{ fontSize: '11px', padding: '3px 8px' }}>✗</button>
                    </div>
                  ) : (
                    <span style={{ cursor: 'pointer' }} onClick={() => { setEditingEmail(user.code); setEditEmailValue(user.email || ''); }} title="Clicca per modificare">
                      {user.email || '-'} ✏️
                    </span>
                  )}
                </td>
                <td>
                  <span className={`role-badge ${user.role}`}>
                    {user.role === 'organizer' ? 'Organizzatore' : `Rapp. - ${user.class}`}
                  </span>
                </td>
                <td><code className="access-code">{user.code}</code></td>
                <td>
                  <div className="action-icons">
                    <button className="btn-secondary" onClick={() => handleRegenerate(user.code)} style={{ fontSize: '12px', padding: '4px 10px' }}>
                      Rigenera
                    </button>
                    <button className="btn-danger" onClick={() => handleDelete(user.code)} style={{ fontSize: '12px', padding: '4px 10px' }}>
                      Elimina
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserManagement;
